import React, { Suspense } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { useRandomGameRoute } from './hooks/useRandomGameRoute';
import { useViewConfig } from './hooks/useViewConfig';
import { useIsFavouriteGamesEnabled } from './hooks/useIsFavouriteGamesEnabled';
import { useSetLayoutMode } from './hooks/useSetLayoutMode';
import * as ROUTE_TEMPLATES from './constants/routeTemplates';
import {
  PARAM_CATEGORY_ID,
  PARAM_PROVIDER_ID,
  PARAM_PAGE_NUMBER,
} from './constants/routeTemplates';
import {
  HEADER_BRAND_CONFIG,
  DASHBOARD_LIST,
  ERROR_PAGE_LIST,
  RECENT_GAMES_LIST,
  SEARCH_PAGE,
} from './constants/viewConfig';
import {
  getBonusesHistoryRoute,
  getCategoryDetailRoute,
  getProviderDetailRoute,
} from './utils/routes';

// common
import Loader from './components/Loader/Loader';
import ErrorBoundary from './components/Error/ErrorBoundary/ErrorBoundary';
import PrivateRoute from './components/PrivateRoute/PrivateRoute';

// Layouts
import TwoColumnLayout from './layouts/TwoColumnLayout/TwoColumnLayout';
import OneColumnLayout from './layouts/OneColumnLayout/OneColumnLayout';

// styles
import './assets/styles/index.css';
import { usePrevious } from './hooks/useCompare';
import { useRouteTransition } from './hooks/useRouteTransition';

// TODO: temporary solution for fixing same issue https://github.com/facebook/create-react-app/issues/5372
// import Dashboard from './pages/Dashboard/Dashboard';

// dynamic pages
// const Dashboard = React.lazy(() => import('./pages/Dashboard/Dashboard'));

const RandomGamePage = React.lazy(() =>
  import('./pages/GamePage/RandomGamePage')
);

const Favorites = React.lazy(() => import('./pages/Favorites/Favorites'));

const GamePage = React.lazy(() => import('./pages/GamePage/GamePage'));

const Categories = React.lazy(() => import('./pages/Categories/Categories'));
const CategoryDetails = React.lazy(() =>
  import('./pages/CategoryDetails/CategoryDetails')
);

const Providers = React.lazy(() => import('./pages/Providers/Providers'));
const ProviderDetails = React.lazy(() =>
  import('./pages/ProviderDetails/ProviderDetails')
);

const CurrentBonuses = React.lazy(() =>
  import('./pages/CurrentBonuses/CurrentBonuses')
);
const BonusesHistory = React.lazy(() =>
  import('./pages/BonusesHistory/BonusesHistory')
);
const BonusDetails = React.lazy(() =>
  import('./pages/BonusDetails/BonusDetails')
);

const BonusGamesWagering = React.lazy(() =>
  import('./pages/BonusGamesWagering/BonusGamesWagering')
);

export const PageErrorFallback = (error) => {
  return <Redirect to={{ pathname: '/500', state: { error: error.error } }} />;
};

const BonusesRoutes = () => {
  useRouteTransition();

  const location = useLocation();
  const prevLocation = usePrevious(location);

  return (
    <Suspense fallback={<Loader />}>
      <Switch>
        <Route
          exact
          path={ROUTE_TEMPLATES.BONUSES_LIST}
          component={CurrentBonuses}
        />

        <PrivateRoute
          exact
          path={ROUTE_TEMPLATES.BONUSES_HISTORY}
          fallback={() => <Redirect to={ROUTE_TEMPLATES.BONUSES_LIST} />}
        >
          <BonusesHistory />
        </PrivateRoute>

        <Route
          exact
          path={[ROUTE_TEMPLATES.BONUS_DETAIL, ROUTE_TEMPLATES.CAMPAIGN_DETAIL]}
          component={(props) => (
            <>
              {prevLocation &&
              prevLocation.pathname === getBonusesHistoryRoute() ? (
                <BonusesHistory />
              ) : (
                <CurrentBonuses />
              )}
              <BonusDetails {...props} prevLocation={prevLocation} />
            </>
          )}
        />
      </Switch>
    </Suspense>
  );
};

const Routes = () => {
  useRouteTransition();
  useSetLayoutMode();
  const randomGameRoute = useRandomGameRoute();
  const isEnabledFavoriteGames = useIsFavouriteGamesEnabled();

  const Header = useViewConfig(HEADER_BRAND_CONFIG);
  const Dashboard = useViewConfig(DASHBOARD_LIST);
  const ErrorPage = useViewConfig(ERROR_PAGE_LIST);
  const RecentGames = useViewConfig(RECENT_GAMES_LIST);
  const SearchPage = useViewConfig(SEARCH_PAGE);

  return (
    <Switch>
      {/* One column layout without Header */}
      <Route path={ROUTE_TEMPLATES.COMMON_ERROR}>
        <OneColumnLayout>
          <ErrorPage />
        </OneColumnLayout>
      </Route>

      {/* One column layout without Header. Game Page */}
      <Route
        path={[ROUTE_TEMPLATES.DEMO_GAME_DETAIL, ROUTE_TEMPLATES.GAME_DETAIL]}
      >
        <ErrorBoundary showDialog fallback={PageErrorFallback}>
          <OneColumnLayout>
            <Suspense fallback={<Loader />}>
              <Switch>
                <Route exact path={randomGameRoute}>
                  <RandomGamePage />
                </Route>

                <Route path={ROUTE_TEMPLATES.GAME_DETAIL}>
                  <GamePage />
                </Route>

                <Route
                  exact
                  path={ROUTE_TEMPLATES.DEMO_GAME_DETAIL}
                  render={(props) => (
                    <Redirect
                      to={ROUTE_TEMPLATES.GAME_DETAIL.replace(
                        ':gameId',
                        props.match.params.gameId
                      )}
                    />
                  )}
                />
              </Switch>
            </Suspense>
          </OneColumnLayout>
        </ErrorBoundary>
      </Route>

      {/* Two column with Header */}
      <Route
        exact
        path={[
          ROUTE_TEMPLATES.CATEGORY_LIST,
          ROUTE_TEMPLATES.CATEGORY_DETAIL,
          ROUTE_TEMPLATES.CATEGORY_DETAIL_PAGINATED,

          ROUTE_TEMPLATES.PROVIDER_LIST,
          ROUTE_TEMPLATES.PROVIDER_DETAIL,
          ROUTE_TEMPLATES.PROVIDER_DETAIL_PAGINATED,

          ROUTE_TEMPLATES.SEARCH,
          ROUTE_TEMPLATES.FAVORITES,
          ROUTE_TEMPLATES.RECENT_GAMES,

          ROUTE_TEMPLATES.BONUSES_LIST,
          ROUTE_TEMPLATES.BONUSES_HISTORY,
          ROUTE_TEMPLATES.BONUS_DETAIL,
          ROUTE_TEMPLATES.CAMPAIGN_DETAIL,

          ROUTE_TEMPLATES.BONUS_GAMES_WAGERING,

          ROUTE_TEMPLATES.ROOT,
        ]}
      >
        <ErrorBoundary showDialog fallback={PageErrorFallback}>
          <TwoColumnLayout header={<Header />}>
            <Suspense fallback={<Loader />}>
              <Switch>
                <Route
                  path={ROUTE_TEMPLATES.CATEGORY_LIST}
                  exact
                  component={Categories}
                />

                <Route
                  path={[
                    ROUTE_TEMPLATES.CATEGORY_DETAIL,
                    ROUTE_TEMPLATES.CATEGORY_DETAIL_PAGINATED,
                  ]}
                >
                  <Switch>
                    <Route
                      path={ROUTE_TEMPLATES.CATEGORY_DETAIL}
                      exact
                      component={CategoryDetails}
                    />

                    <Route
                      path={ROUTE_TEMPLATES.CATEGORY_DETAIL_PAGINATED}
                      render={(props) => {
                        const page = props.match.params[PARAM_PAGE_NUMBER];
                        const categoryId =
                          props.match.params[PARAM_CATEGORY_ID];

                        // eslint-disable-next-line
                        return page && page != 1 ? (
                          <CategoryDetails {...props} />
                        ) : (
                          <Redirect to={getCategoryDetailRoute(categoryId)} />
                        );
                      }}
                    />
                  </Switch>
                </Route>

                <Route
                  path={ROUTE_TEMPLATES.PROVIDER_LIST}
                  exact
                  component={Providers}
                />

                <Route
                  path={[
                    ROUTE_TEMPLATES.PROVIDER_DETAIL,
                    ROUTE_TEMPLATES.PROVIDER_DETAIL_PAGINATED,
                  ]}
                >
                  <Switch>
                    <Route
                      path={ROUTE_TEMPLATES.PROVIDER_DETAIL}
                      exact
                      component={ProviderDetails}
                    />

                    <Route
                      path={ROUTE_TEMPLATES.PROVIDER_DETAIL_PAGINATED}
                      render={(props) => {
                        const page = props.match.params[PARAM_PAGE_NUMBER];
                        const providerId =
                          props.match.params[PARAM_PROVIDER_ID];

                        // eslint-disable-next-line
                        return page && page != 1 ? (
                          <ProviderDetails {...props} />
                        ) : (
                          <Redirect to={getProviderDetailRoute(providerId)} />
                        );
                      }}
                    />
                  </Switch>
                </Route>

                <Route
                  path={ROUTE_TEMPLATES.BONUS_GAMES_WAGERING}
                  exact
                  component={BonusGamesWagering}
                />

                <Route
                  path={ROUTE_TEMPLATES.SEARCH}
                  exact
                  component={SearchPage}
                />

                <PrivateRoute
                  path={ROUTE_TEMPLATES.FAVORITES}
                  exact
                  fallback={() => <Redirect to={ROUTE_TEMPLATES.ROOT} />}
                >
                  {isEnabledFavoriteGames ? (
                    <Favorites />
                  ) : (
                    <Redirect to={ROUTE_TEMPLATES.ROOT} />
                  )}
                </PrivateRoute>
                <PrivateRoute
                  path={ROUTE_TEMPLATES.RECENT_GAMES}
                  exact
                  fallback={() => <Redirect to={ROUTE_TEMPLATES.NOT_FOUND} />}
                >
                  <RecentGames />
                </PrivateRoute>

                <Route
                  path={[
                    ROUTE_TEMPLATES.BONUSES_LIST,
                    ROUTE_TEMPLATES.BONUSES_HISTORY,
                    ROUTE_TEMPLATES.BONUS_DETAIL,
                    ROUTE_TEMPLATES.CAMPAIGN_DETAIL,
                  ]}
                  exact
                  component={BonusesRoutes}
                />

                <Route
                  exact
                  path={ROUTE_TEMPLATES.ROOT}
                  component={Dashboard}
                />
              </Switch>
            </Suspense>
          </TwoColumnLayout>
        </ErrorBoundary>
      </Route>

      {/* Not Found */}
      <Route path={ROUTE_TEMPLATES.NO_MATCH}>
        <OneColumnLayout>
          <ErrorPage status={404} />
        </OneColumnLayout>
      </Route>
    </Switch>
  );
};

export default Routes;
